import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface BookingDetails {
  name: string;
  email: string;
  phone: string;
  topics: string[];
  platform: string;
  selectedTimes: string;
  additionalInfo: string;
}

const MeetingConfirmation = () => {
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);

  useEffect(() => {
    const savedBooking = sessionStorage.getItem('meetingBooking');
    if (savedBooking) {
      setBookingDetails(JSON.parse(savedBooking));
    }
  }, []);

  return (
    <div className="container-custom max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="text-center mb-8">
          <svg className="w-20 h-20 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2m0 0 6-2m0 0 6 2" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold mb-4 text-center">Booking Confirmed</h1>
        <p className="text-lg text-gray-600 text-center">
          Thank you for booking a 30-minute consultation.
        </p>
        <div className="space-y-2">
          <p className="text-gray-600">
            <svg className="w-5 h-5 text-green-600 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l9 9 9 0 11 21l-7 7-7 7 11a5 5 0 0 0-7 7z" />
            </svg>
            We will review your preferred time slots and contact you via email within 24 hours.
          </p>
          <p className="text-gray-600">
            <svg className="w-5 h-5 text-blue-600 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l9 9 9 0 11 21l-7 7-7 7 11a5 5 0 0 0-7 7z" />
            </svg>
            Confirmation email has been sent to your email.
          </p>
          <p className="text-gray-600">
            <svg className="w-5 h-5 text-blue-600 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l9 9 9 0 11 21l-7 7-7 7 11a5 5 0 0 0-7 7z" />
            </svg>
            Please check your email for the final confirmation.
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <h2 className="font-bold text-green-900 mb-3">What Happens Next?</h2>
          <ol className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="font-bold mr-2">1.</span>
              <span>We will confirm your preferred time slots and contact you via email within 24 hours.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">2.</span>
              <span>We will confirm the best available time slot and send you meeting platform details.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">3.</span>
              <span>Confirmation email has been sent to your email.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">4.</span>
              <span>Please check your email for the final confirmation.</span>
            </li>
          </ol>
        </div>
        {bookingDetails && (
          <div className="border border-gray-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Your Booking Details</h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Contact Information</h3>
                <p className="text-gray-600">
                  <strong>Name:</strong> {bookingDetails.name}<br />
                  <strong>Email:</strong> {bookingDetails.email}<br />
                  <strong>Phone:</strong> {bookingDetails.phone}
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Topics</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  {bookingDetails.topics.map((topic, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Meeting Platform</h3>
                <p className="text-gray-600 capitalize">{bookingDetails.platform}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Preferred Time Slots</h3>
                <p className="text-gray-600 whitespace-pre-line">{bookingDetails.selectedTimes}</p>
              </div>

              {bookingDetails.additionalInfo && (
                <div>
                  <h3 className="font-semibold text-gray-700">Additional Information</h3>
                  <p className="text-gray-600">{bookingDetails.additionalInfo}</p>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
          <h2 className="font-bold text-yellow-900 mb-2">Important Notes</h2>
          <ul className="space-y-2 text-yellow-800 text-sm">
            <li>• Please check your email regularly for meeting confirmation and payment instructions.</li>
            <li>• Your booking will be confirmed after we review your request.</li>
            <li>• If you need to reschedule, please contact us at least 24 hours in advance.</li>
            <li>• The consultation covers general guidance. For medical advice, please consult with qualified healthcare professionals.</li>
          </ul>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <Link
            to="/services"
            className="flex-1 btn-secondary text-center"
          >
            Return to Services
          </Link>
          <Link
            to="/"
            className="flex-1 btn-primary text-center"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MeetingConfirmation;
